import React, {
  MouseEvent,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import {
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import StatusTag from 'core/components/StatusTag';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  useMedia,
} from 'core/hooks';
import {
  TempoPatientRowDTO,
} from 'dtos';
import Name from 'pages/Dashboard/components/Name';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  useNavigate,
} from 'react-router-dom';
import {
  formatDate,
  formatTime,
} from 'utils/date';
import clsx from 'clsx';

const tempoStatusMap = {
  pending: 'Pending',
  inprogress: 'Sent',
  reminder: 'Reminder',
  success: 'Completed',
  completed: 'Completed',
};

type StatusCellProps = {
  status?: string | null;
};

type PatientCellProps = {
  row: Row<TempoPatientRowDTO>;
};

function StatusCell({ status }: StatusCellProps) {
  const isCompleted = status?.toLowerCase() === 'completed' || status?.toLowerCase() === 'success';

  return notEmptyString(status) ? (
    <div className="flex flex-col items-start gap-1">
      <StatusTag
        value={status ?? ''}
        statusMap={tempoStatusMap}
        className={clsx(
          '!h-6 !px-2 !inline-flex !items-center',
          { '!bg-green-200': isCompleted },
        )}
        classes={{
          root: clsx('!inline-flex !items-center', { '!bg-green-200': isCompleted }),
          value: clsx('!gap-1 !items-center !text-xs !leading-4', { '!text-green-700': isCompleted }),
        }}
      />
    </div>
  ) : DEFAULT_EMPTY_VALUE;
}

function PatientCell({ row }: PatientCellProps) {
  const { getMediaPathById } = useMedia();
  const navigate = useNavigate();
  const patientName = getDisplayName(row.original.patient?.formattedNames, DEFAULT_EMPTY_VALUE);
  const patientId = row.original.patient?.patientId;
  const link = getPatientChartPath(patientId);

  return (
    <Name
      link={{
        to: link ?? undefined,
        onClick: (event: MouseEvent<HTMLAnchorElement>) => {
          event.stopPropagation();
          if (!isNil(link)) {
            navigate(link);
          }
        },
      }}
      name={patientName}
      image={{ src: getMediaPathById(patientId ?? 0) ?? undefined }}
      classes={{
        root: '!min-w-0 !font-normal',
        nameRoot: '!min-w-0',
        label: '!text-xs !font-medium !leading-4',
        link: '!w-fit',
      }}
    />
  );
}

type DateTimeCellProps = {
  value?: string | null;
};

function DateTimeCell({ value }: DateTimeCellProps) {
  return !notEmptyString(value) ? DEFAULT_EMPTY_VALUE : (
    <div className="flex flex-col items-start gap-1">
      <span className="text-xs font-medium leading-4 text-gray-700">
        {formatDate(value ?? '')}
      </span>
      <span className="text-xs font-normal leading-4 text-gray-500">
        {formatTime(value ?? '')}
      </span>
    </div>
  );
}

type CountCellProps = {
  value?: number | null;
};

function CountCell({ value }: CountCellProps) {
  return (
    <span className="text-xs text-center w-full font-medium leading-4 text-gray-700">
      {value ?? DEFAULT_EMPTY_VALUE}
    </span>
  );
}

const columns: ColumnDef<TempoPatientRowDTO>[] = [{
  id: 'patient',
  accessorFn: (row) => getDisplayName(row.patient?.formattedNames),
  header: 'Patient',
  minSize: 190,
  meta: { autoSized: true },
  cell: ({ row }) => <PatientCell row={row} />,
}, {
  id: 'day1AttemptCount',
  accessorKey: 'day1AttemptCount',
  header: 'Day 1 attempts',
  size: 105,
  cell: ({ row }) => <CountCell value={row.original.day1AttemptCount} />,
}, {
  id: 'day1Status',
  accessorKey: 'day1Status',
  header: 'Day 1 status',
  size: 125,
  cell: ({ row }) => (
    <StatusCell status={row.original.day1Status} />
  ),
}, {
  id: 'day2AttemptCount',
  accessorKey: 'day2AttemptCount',
  header: 'Day 2 attempts',
  size: 105,
  cell: ({ row }) => <CountCell value={row.original.day2AttemptCount} />,
}, {
  id: 'day2Status',
  accessorKey: 'day2Status',
  header: 'Day 2 status',
  size: 125,
  cell: ({ row }) => (
    <StatusCell status={row.original.day2Status} />
  ),
}, {
  id: 'lastActivityOn',
  accessorKey: 'lastActivityOn',
  header: 'Last activity',
  size: 120,
  cell: ({ row }) => <DateTimeCell value={row.original.lastActivityOn} />,
}];

export default columns;
